/**
 * Phone discipline (dashboard). Customer phones are stored canonical, as a
 * single E.164 string (`+491701112233`) - that's the per-franchise identity
 * key. Presentation is *derived* from it, never stored. This is the single
 * seam for phone formatting on the dashboard; mirrors `@/utils/money`
 * (store canonical → format on read).
 */

import { parsePhoneNumberFromString, type CountryCode } from 'libphonenumber-js';

/** Default parse region - mirrors the backend's `DEFAULT_PHONE_REGION` (DE). */
const DEFAULT_PHONE_REGION = 'DE';

/**
 * Minimum national (significant) digits a guest phone must have, excluding the
 * country/dial code. German numbers run to at least 10 digits including the
 * +49 country code - i.e. ≥7 nationally - so we hold every guest number to that
 * floor. Without it a 4-digit number passes (the backend stores it raw when it
 * can't normalize), which is never a reachable phone.
 */
export const MIN_PHONE_NATIONAL_DIGITS = 7;

/** Canonical E.164 → grouped, readable international form. Raw on failure. */
export function formatPhone(e164: string): string {
  if (!e164) return '';
  const parsed = parsePhoneNumberFromString(e164);
  return parsed ? parsed.formatInternational() : e164;
}

/**
 * Is `raw` a usable guest phone for `region` (ISO2, e.g. "DE")? Parses out the
 * national significant number (country code excluded) and requires at least
 * MIN_PHONE_NATIONAL_DIGITS digits. Unparseable input yields an empty national
 * number, so it's rejected too. Pass the restaurant's country so local-format
 * numbers ("030 12345678") parse correctly.
 */
export function isValidGuestPhone(raw: string, region?: string): boolean {
  const trimmed = raw.trim();
  if (!trimmed) return false;
  const parsed = parsePhoneNumberFromString(
    trimmed,
    (region?.toUpperCase() as CountryCode) || DEFAULT_PHONE_REGION,
  );
  return (parsed?.nationalNumber.length ?? 0) >= MIN_PHONE_NATIONAL_DIGITS;
}
